import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  activateConversationStream,
  addMessage,
  createConversation,
  getConversation,
  selectRoutingAssistantConversations,
  selectRoutingAssistantLastConversationId,
} from '@/features/domain/routing-assistant'
import { useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'

export const useConversation = () => {
  const conversations = useSelector(selectRoutingAssistantConversations)
  const dispatch = useAppDispatch()
  const toast = useNotification()
  const lastConversationId = useSelector(selectRoutingAssistantLastConversationId)

  const [conversationId, setConversationId] = useState<string | null>(lastConversationId ?? 'local')
  const [sending, setSending] = useState(false)

  const fetchConversation = useCallback(async () => {
    try {
      if (!conversationId) return
      const response = await dispatch(getConversation({ conversationId }))

      if (getConversation.rejected.match(response)) {
        throw new Error(response.payload?.message || 'Failed to fetch conversation')
      }
    } catch (e: any) {
      toast.error(String(e?.message || e))
    }
  }, [conversationId])

  const sendMessage = useCallback(
    async (text: string) => {
      const id = `m-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
      const message: uui.domain.server.rm.routingAssistant.AgentInputMessage = {
        content: text,
        id,
      }

      if (!conversationId || conversationId === 'local') {
        await createNewConversation(message)
        return
      }

      setSending(true)

      try {
        const response = await dispatch(
          addMessage({
            conversationId,
            message,
          }),
        )

        if (addMessage.rejected.match(response)) {
          throw new Error(response.payload?.message || 'Failed to fetch conversation')
        }
      } catch (e: any) {
        toast.error(String(e?.message || e))
      } finally {
        setSending(false)
      }
    },
    [conversationId, fetchConversation],
  )

  const createNewConversation = useCallback(
    async (message: uui.domain.server.rm.routingAssistant.AgentInputMessage) => {
      const messages = [message]
      try {
        const response = await dispatch(createConversation({ messages }))

        if (createConversation.rejected.match(response)) {
          throw new Error(response.payload?.message || 'Failed to create conversation')
        }
        setConversationId(response.payload.id)
      } catch (e: any) {
        toast.error(String(e?.message || e))
      }
    },
    [],
  )

  const clearConversation = useCallback(() => {
    setConversationId('local')
  }, [setConversationId])

  useEffect(() => {
    if (!conversationId || conversationId === 'local') return

    dispatch(activateConversationStream({ conversationId }))
  }, [activateConversationStream, conversationId, dispatch])

  return {
    conversation: conversationId ? conversations[conversationId] : null,
    fetchConversation,
    clearConversation,
    sending,
    sendMessage,
  }
}
